import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { moduleBuilderApi } from '@/api/services/module-builder.api';
import { usePermissions } from '@/hooks/use-permissions';
import { PERMISSION } from '@/features/users-roles/constants';
import { MODULE_BUILDER_QUERY_KEYS } from '@/features/module-builder/constants';
import { getModuleIcon } from '@/features/module-builder/module-icons';
import type { IModuleSettings } from '@/features/module-builder/types';
import type { NavItem } from './app-sidebar';

/**
 * Dynamic "Modules" nav section: every ENABLED custom module, ordered by its
 * configured position. Shares the builder's `modules()` query key, so
 * enabling/reordering modules in Module Setup updates the nav automatically.
 * Hidden entirely when the user lacks module permissions or no custom module
 * is enabled.
 */
export function useModuleNavItems(): { section: NavItem | null; isLoading: boolean } {
    const { t } = useTranslation();
    const { hasPermission, isLoading: permissionsLoading } = usePermissions();

    // Listing modules needs modules:read; record pages need module-records:read.
    const canSee =
        !permissionsLoading &&
        hasPermission(PERMISSION.MODULES_READ) &&
        hasPermission(PERMISSION.MODULE_RECORDS_READ);

    const { data: modules, isLoading } = useQuery({
        queryKey: MODULE_BUILDER_QUERY_KEYS.modules(),
        queryFn: () => moduleBuilderApi.listModules(),
        enabled: canSee,
        staleTime: 30_000,
    });

    const section = useMemo<NavItem | null>(() => {
        if (!canSee || !modules) return null;
        const items = modules
            .filter((module) => {
                if (module.moduleType !== 'CUSTOM' || !module.isActive) return false;
                const settings = (module.settings ?? {}) as IModuleSettings;
                return settings.showInNav !== false;
            })
            .sort((a, b) => a.position - b.position)
            .map((module) => ({
                title: module.displayName,
                url: `/modules/${module.slug}/records`,
                icon: getModuleIcon(module.icon ?? undefined),
                iconColor: module.color ?? undefined,
            }));
        if (items.length === 0) return null;
        return {
            isSection: true,
            title: t('nav.sections.modules', 'Modules'),
            collapseKey: 'modules',
            items,
        };
    }, [canSee, modules, t]);

    return { section, isLoading: canSee && isLoading };
}
